import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  TrendingUp,
  Home,
  Users,
  Zap,
  Wrench,
  Globe,
  Percent,
  List,
  X,
  Copy,
  Check,
  Calendar,
  User,
  FileText,
  Hash,
  Clock,
  ChevronRight,
  Inbox,
  UtensilsCrossed,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Category metadata — icon / color used inside the overlay headers   */
/*  Keep this in sync with whatever categories your app supports.      */
/* ------------------------------------------------------------------ */

const CATEGORY_META = {
  marketing: { label: "Marketing", icon: TrendingUp, color: "#e8853a", bg: "#fdf0e3" },
  rent: { label: "Rent", icon: Home, color: "#16a394", bg: "#e4f7f4" },
  salary: { label: "Salary", icon: Users, color: "#2fa86b", bg: "#e6f7ed" },
  bills: { label: "Bills", icon: Zap, color: "#4A90E2", bg: "#eaf2fd" },
  repairs: { label: "Repairs", icon: Wrench, color: "#d4a32c", bg: "#fbf3df" },
  onlineads: { label: "Online Ads", icon: Globe, color: "#8b5cf6", bg: "#f2eefe" },
  tax: { label: "Tax", icon: Percent, color: "#e15a5a", bg: "#fdebeb" },
  food: { label: "Food", icon: UtensilsCrossed, color: "rgba(132, 204, 22, 1)", bg: "rgba(132, 204, 22, 0.15)" },
  others: { label: "Others", icon: List, color: "#64748b", bg: "#eef1f4" },
};

function getCategoryMeta(category) {
  const key = category.replace(/\s+/g, "").toLowerCase();

  return CATEGORY_META[key] || CATEGORY_META.others;
}

/* ------------------------------------------------------------------ */
/*  Formatters                                                         */
/* ------------------------------------------------------------------ */

function formatAmount(amount, currency = "NGN") {
  return `${currency} ${Number(amount || 0).toLocaleString("en-US")}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date}, ${time}`;
}

function sumAmounts(expenses) {
  return expenses.reduce((total, e) => total + Number(e.amount || 0), 0);
}

/* ------------------------------------------------------------------ */
/*  Shared sheet shell — portal, backdrop, slide-up animation, Esc key */
/* ------------------------------------------------------------------ */

function Sheet({ open, onClose, zIndex, children }) {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClosing(false);
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  function handleClose() {
    setClosing(true);
    setMounted(false);
    setTimeout(onClose, 200);
  }

  return createPortal(
    <>
      {/* In production move this into ExpenseDetailOverlay.css and import
          it normally — it's inlined here only so the component is fully
          self-contained / drop-in. */}
      <style>{STYLES}</style>
      <div className={`exd__backdrop ${mounted && !closing ? "exd__backdrop--show" : ""}`} style={{ zIndex }} onClick={handleClose}>
        <div className={`exd__sheet ${mounted && !closing ? "exd__sheet--show" : ""}`} onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
          <div className='exd__handle' />
          {children(handleClose)}
        </div>
      </div>
    </>,
    document.body
  );
}

/* ------------------------------------------------------------------ */
/*  DetailRow — one label/value line                                   */
/* ------------------------------------------------------------------ */

function DetailRow({ icon: Icon, label, value, copyable }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className='exd__row'>
      <div className='exd__rowLeft'>
        <Icon size={15} className='exd__rowIcon' />
        <span className='exd__rowLabel'>{label}</span>
      </div>
      <div className='exd__rowRight'>
        <span className='exd__rowValue' title={value}>
          {value || "—"}
        </span>
        {copyable && value && (
          <button type='button' className='exd__copyBtn' onClick={handleCopy} aria-label={`Copy ${label}`}>
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ExpenseDetailOverlay — single expense, full detail                 */
/*                                                                       */
/*  <ExpenseDetailOverlay expense={selectedExpense} onClose={...} />    */
/*  Pass null/undefined as `expense` to keep it closed.                 */
/* ------------------------------------------------------------------ */

export function ExpenseDetailOverlay({ expense, onClose, currency = "NGN" }) {
  return (
    <Sheet open={!!expense} onClose={onClose} zIndex={1100}>
      {(close) => {
        if (!expense) return null;
        const meta = getCategoryMeta(expense.category);
        const Icon = meta.icon;
        const showUpdated = expense.updatedAt && expense.updatedAt !== expense.createdAt;
        return (
          <>
            <div className='exd__header'>
              <div className='exd__headerLeft'>
                <span className='exd__iconWrap' style={{ background: meta.bg }}>
                  <Icon size={20} color={meta.color} strokeWidth={2} />
                </span>
                <span className='exd__categoryPill' style={{ color: meta.color, background: meta.bg }}>
                  {meta.label}
                </span>
              </div>
              <button type='button' className='exd__closeBtn' onClick={close} aria-label='Close'>
                <X size={18} />
              </button>
            </div>
            <h2 className='exd__title'>{expense.name}</h2>
            <div className='exd__amount'>{formatAmount(expense.amount, currency)}</div>
            <div className='exd__divider' />
            <div className='exd__detailList'>
              <DetailRow icon={FileText} label='Description' value={expense.description} />
              <DetailRow icon={Calendar} label='Date' value={formatDate(expense.date)} />
              <DetailRow icon={User} label='Added by' value={expense.addedBy?.fullName} />
              <DetailRow icon={Hash} label='Store ID' value={expense.storeID} copyable />
              <DetailRow icon={Clock} label='Created' value={formatDateTime(expense.createdAt)} />
              {showUpdated && <DetailRow icon={Clock} label='Last updated' value={formatDateTime(expense.updatedAt)} />}
            </div>
          </>
        );
      }}
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  CategoryExpensesOverlay — list (array) of registered expenses for  */
/*  a category. Clicking a row drills into ExpenseDetailOverlay.        */
/*                                                                       */
/*  <CategoryExpensesOverlay                                            */
/*    category={openCategory}      // e.g. "rent", or null when closed  */
/*    expenses={expensesForThatCategory}  // array, same shape as below */
/*    onClose={() => setOpenCategory(null)}                             */
/*  />                                                                   */
/*                                                                       */
/*  Expense shape expected:                                             */
/*    { addedBy: { fullName }, amount, category, createdAt, date,       */
/*      description, name, storeID, updatedAt }                         */
/* ------------------------------------------------------------------ */

export function CategoryExpensesOverlay({ category = null, expenses = [], onClose, currency = "NGN" }) {
  const [activeExpense, setActiveExpense] = useState(null);

  // reset the drilled-in expense whenever the category sheet closes
  useEffect(() => {
    if (!category) setActiveExpense(null);
  }, [category]);

  const meta = category ? getCategoryMeta(category) : null;
  const Icon = meta?.icon;
  const total = sumAmounts(expenses);

  return (
    <>
      <Sheet open={!!category} onClose={onClose} zIndex={1000}>
        {(close) => (
          <>
            <div className='exd__header'>
              <div className='exd__headerLeft'>
                <span className='exd__iconWrap' style={{ background: meta.bg }}>
                  <Icon size={20} color={meta.color} strokeWidth={2} />
                </span>
                <span className='exd__categoryPill' style={{ color: meta.color, background: meta.bg }}>
                  {meta.label}
                </span>
              </div>
              <button type='button' className='exd__closeBtn' onClick={close} aria-label='Close'>
                <X size={18} />
              </button>
            </div>

            <div className='exd__listHeader'>
              <span className='exd__listCount'>
                {expenses.length} {expenses.length === 1 ? "expense" : "expenses"}
              </span>
              <span className='exd__listTotal'>{formatAmount(total, currency)}</span>
            </div>

            <div className='exd__divider' />

            {expenses.length === 0 ? (
              <div className='exd__empty'>
                <Inbox size={28} color='#c4ccd6' strokeWidth={1.5} />
                <p>No expenses recorded for this category yet.</p>
              </div>
            ) : (
              <div className='exd__expenseList'>
                {expenses.map((expense, i) => (
                  <button type='button' key={i} className='exd__expenseRow' onClick={() => setActiveExpense(expense)}>
                    <span className='exd__expenseRowText'>
                      <span className='exd__expenseRowName'>{expense.name}</span>
                      {expense.description && <span className='exd__expenseRowDesc'>{expense.description}</span>}
                      <span className='exd__expenseRowMeta'>
                        {formatDate(expense.date)} · {expense.addedBy?.fullName || "Unknown"}
                      </span>
                    </span>
                    <span className='exd__expenseRowRight'>
                      <span className='exd__expenseRowAmount'>{formatAmount(expense.amount, currency)}</span>
                      <ChevronRight size={16} color='#c4ccd6' />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Sheet>

      <ExpenseDetailOverlay expense={activeExpense} onClose={() => setActiveExpense(null)} currency={currency} />
    </>
  );
}

export default CategoryExpensesOverlay;

/* ------------------------------------------------------------------ */
/*  Styles — inlined via the Sheet component so this file is fully     */
/*  drop-in. In your codebase, move this into ExpenseDetailOverlay.css */
/*  and import it the normal way like your other components.           */
/* ------------------------------------------------------------------ */

const STYLES = `
.exd__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 27, 46, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.exd__backdrop--show { opacity: 1; }

.exd__sheet {
  width: 100%;
  max-width: 440px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  padding: 12px 22px 28px;
  box-shadow: 0 -8px 30px rgba(15, 27, 46, 0.18);
  transform: translateY(100%);
  transition: transform 0.22s cubic-bezier(0.32, 0.72, 0, 1);
  
  overflow: hidden;
}

.exd__sheet--show { transform: translateY(0); }

@media (min-width: 480px) {
  .exd__backdrop { align-items: center; }
  .exd__sheet { border-radius: 20px; }
}

.exd__handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #e4e8ed;
  margin: 0 auto 16px;
  flex-shrink: 0;
}

.exd__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.exd__headerLeft {
  display: flex;
  align-items: center;
  gap: 10px;
}

.exd__iconWrap {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.exd__categoryPill {
  font-size: 12.5px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 100px;
}

.exd__closeBtn {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: none;
  background: #f3f5f7;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.exd__closeBtn:hover { background: #e9ecef; }

.exd__title {
  font-size: 17px;
  font-weight: 700;
  color: #1e3a5f;
  margin: 0 0 6px;
}

.exd__amount {
  font-size: 28px;
  font-weight: 700;
  color: #1e3a5f;

  margin-bottom: 18px;
}

.exd__divider {
  height: 1px;
  background: #eef0f3;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.exd__detailList {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.exd__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 0;
  border-bottom: 1px solid #f4f5f7;
}

.exd__row:last-child { border-bottom: none; }

.exd__rowLeft {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: #97a3b3;
}

.exd__rowIcon { color: #b0bac6; }

.exd__rowLabel {
  font-size: 13px;
  color: #97a3b3;
}

.exd__rowRight {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.exd__rowValue {
 font-size: 13px;
font-weight: 500;
color: #1e3a5f;
text-align: right;
overflow: hidden;
display: -webkit-box;
-webkit-line-clamp: 4;
-webkit-box-orient: vertical;
line-clamp: 4;
max-width: 220px;
word-break: break-word;
  
}


.exd__copyBtn {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  border: none;
  background: #f3f5f7;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.exd__copyBtn:hover { background: #e9ecef; }

.exd__listHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.exd__listCount {
  font-size: 13px;
  color: #97a3b3;
}

.exd__listTotal {
  font-size: 17px;
  font-weight: 700;
  color: #1e3a5f;
 
}

.exd__expenseList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding-bottom: 4px;
}

.exd__expenseRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #fafbfc;
  border: 1px solid #f0f2f4;
  border-radius: 14px;
  padding: 13px 14px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.exd__expenseRow:hover {
  background: #f4f6f8;
  border-color: #e6e9ec;
}

.exd__expenseRowText {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.exd__expenseRowName {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.exd__expenseRowDesc {
  font-size: 12.5px;
  color: #6b7686;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.exd__expenseRowMeta {
  font-size: 12px;
  color: #9aa5b3;
}

.exd__expenseRowRight {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.exd__expenseRowAmount {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e3a5f;
 
}

.exd__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 30px 10px 10px;
  color: #9aa5b3;
  font-size: 13px;
}
`;
